import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import Tab from '@mui/material/Tab';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { CircularProgress, Drawer } from '@mui/material';
import ReactFamilyTree from 'react-family-tree';
import calcTree from 'relatives-tree';
import domtoimage from 'dom-to-image-more';
import { Header } from '../../dashboard/components';
import type { Node, ExtNode } from 'relatives-tree/lib/types';
import {
  MAX_SCALE,
  MIN_SCALE,
  NODE_HEIGHT,
  NODE_WIDTH,
} from '../components/constants';
import { TreeWrapper } from '../components/TreeWrapper';
import EditFamilyMember from '../components/EditFamilyMember';
import AddMemberDialog from '../components/AddMemberDialog';
import {
  useGetFamilyTree,
  useAddMemberFamilyTree,
  useDeleteFamilyTreeMember,
  useDeleteFamilyTree,
  useUpdateTree,
  useAddCollaborators,
  useExportGedcom,
} from '../../../hooks/treeHooks';
import {
  Person,
  MemberType,
  AddMemberPayload,
  DeleteMemberPayload,
  UpdateTreePayload,
  AddCollaboratorsPayload,
} from '../../../types/tree';
import { FamilyNode } from '../components/FamilyNode';
import { transformNodeData } from '../../../utils/transformTree';
import queryClient from '../../../core/http/react-query';
import FamilyTreeOverview from '../components/FamilyOverview';
import { useModal } from '../../../components/common';
import MemberSearch from '../components/MemberSearch';
import DeleteMember from '../components/DeleteMember';
import DeleteTree from '../components/DeleteTree';
import AddCollaborators from '../components/AddCollaborators';
import { FamilyStory } from '../components';
import { AppConfig, maxFamilyMembers } from '../../../core/constants';
import ErrorDisplay from '../../../components/common/ErrorDisplay';
import MigrationTracking from '../components/tracking/MigrationTracking';
import { useAuth } from '../../../components/auth/AuthProvider';
import { isTreeOwner } from '../components';
import SubscribeModal from '../../../components/common/SubscribeModal';
import useSubscription from '../../../hooks/useSubscription';
import useIsAdmin from '../../../hooks/useIsAdmin';
import {
  getTreeBounds,
  limitNodesByDepth,
  LARGE_TREE_THRESHOLD,
  DEFAULT_LARGE_TREE_DEPTH,
} from '../../../utils/treeGraph';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';

export default function FamilyTreePage() {
  const { treeId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { palette } = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { openModal, closeModal } = useModal();
  const { isPremium } = useSubscription();
  const navigate = useNavigate();

  const { t } = useTranslation('tree');
  const { currentUser } = useAuth();
  const { isAdmin } = useIsAdmin(currentUser);

  const [value, setValue] = useState<string>('1');
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [isDrawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [nodes, setNodes] = useState<Node[]>([]);
  const [firstRootId, setFirstRootId] = useState<string | null>(null);
  const [rootId, setRootId] = useState<string | null>(null);
  // undefined = automatic (limited only for very large trees); null = all
  const [depthChoice, setDepthChoice] = useState<number | null | undefined>(
    undefined,
  );
  const maxDepth =
    depthChoice === undefined
      ? nodes.length > LARGE_TREE_THRESHOLD
        ? DEFAULT_LARGE_TREE_DEPTH
        : null
      : depthChoice;
  const displayNodes = useMemo(
    () => limitNodesByDepth(nodes, rootId, maxDepth),
    [nodes, rootId, maxDepth],
  );
  const treeLayout = useMemo(
    () => (rootId ? calcTree(displayNodes, { rootId }) : null),
    [displayNodes, rootId],
  );
  const treeBounds = useMemo(
    () => getTreeBounds(treeLayout?.nodes ?? [], NODE_WIDTH, NODE_HEIGHT),
    [treeLayout],
  );
  const [selectedNarrativeType, setSelectedNarrativeType] = useState<
    'story' | 'journey' | null
  >(null);
  const [selectedNarrativeId, setSelectedNarrativeId] = useState<string>('');
  const [quickAddOpen, setQuickAddOpen] = useState<boolean>(false);
  const [quickAddRelation, setQuickAddRelation] = useState<string>('CHILD');
  const [quickAddSelectedPerson, setQuickAddSelectedPerson] = useState<
    Person | undefined
  >(undefined);

  const treeRef = useRef<HTMLDivElement | null>(null);

  const isPaidUser = isPremium || isAdmin;

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  const openDrawer = (nodeId: string | null) => {
    setSelectedNode(nodeId);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  // Stops the tree query from refetching a deleted tree (404) while navigating away.
  const [isTreeDeleted, setIsTreeDeleted] = useState<boolean>(false);

  const { data, isLoading, isError, error, refetch } = useGetFamilyTree(
    treeId ?? '',
    !isTreeDeleted,
  );
  const mutation = useAddMemberFamilyTree(treeId ?? '');
  const deleteTreeMemberMutation = useDeleteFamilyTreeMember(treeId ?? '');
  const deleteFamilyTreeMutation = useDeleteFamilyTree(treeId ?? '');
  const updateTreeMutation = useUpdateTree(treeId ?? '');
  const addCollaboratorsMutation = useAddCollaborators(treeId ?? '');
  const { mutateAsync: exportGedcom } = useExportGedcom();

  useEffect(() => {
    if (data) {
      const tree = data as any;
      const members = tree?.members ?? [];
      const nodesData = transformNodeData(members);
      setNodes(nodesData);

      let initialRootId: string | undefined;
      if (
        tree?.root_member_id &&
        members.some((m: Person) => m.id === tree.root_member_id)
      ) {
        initialRootId = tree.root_member_id;
      } else {
        initialRootId = members?.filter(
          (person: Person) => person.father_id && person.mother_id,
        )[0]?.id;
      }

      if (!initialRootId && members.length > 0) {
        initialRootId = members[0].id;
      }

      setFirstRootId(initialRootId ?? null);
      setRootId((prev) =>
        prev && members.some((m: Person) => m.id === prev)
          ? prev
          : (initialRootId ?? null),
      );
    }
  }, [data]);

  const familyTree = useMemo(() => {
    if (data) {
      return data as any;
    }
    return null;
  }, [data]);

  const isOwner = isTreeOwner(familyTree, currentUser);
  const tabParam = searchParams.get('tab');
  const actionParam = searchParams.get('action');
  const shouldAutoOpenStory =
    isOwner && tabParam === 'stories' && actionParam === 'create';
  const shouldAutoOpenJourney =
    isOwner && tabParam === 'journeys' && actionParam === 'create';

  useEffect(() => {
    if (!isOwner) return;
    if (tabParam === 'stories') {
      setValue('3');
    } else if (tabParam === 'journeys') {
      setValue('4');
    }
  }, [isOwner, tabParam]);

  // Strip action=create from the URL after it has been consumed so that
  // closing the modal and re-navigating to the tab does not re-open it.
  useEffect(() => {
    if (actionParam !== 'create') return;
    const next = new URLSearchParams(searchParams);
    next.delete('action');
    setSearchParams(next, { replace: true });
  }, [actionParam, searchParams, setSearchParams]);

  const resetRootHandler = useCallback(
    () => setRootId(firstRootId),
    [firstRootId],
  );

  const handleAddMember = useCallback(
    async (payload: AddMemberPayload) => {
      const { _keepOpen, ...apiPayload } = payload as any;
      try {
        const updatedTree = await mutation.mutateAsync(apiPayload);
        enqueueSnackbar('Family member added successfully!', {
          variant: 'success',
        });
        if (updatedTree) {
          queryClient.setQueryData(['familyTrees', treeId], updatedTree);
        } else {
          queryClient.invalidateQueries({
            queryKey: ['familyTrees', treeId],
            exact: true,
          });
        }
        return updatedTree;
      } catch (err) {
        enqueueSnackbar('Failed to add family member', {
          variant: 'error',
        });
        console.error('Error adding member:', err);
        throw err;
      }
    },
    [mutation, treeId, enqueueSnackbar],
  );

  const handleQuickAdd = useCallback(
    (relation: MemberType, nodeDetails?: Person) => {
      const isFamilyMemberLimitReached = familyTree?.members
        ? familyTree.members.length >= maxFamilyMembers
        : false;

      if (isFamilyMemberLimitReached && !isPaidUser) {
        return openModal(
          <SubscribeModal
            closeModal={closeModal}
            handleClose={closeModal}
            message={`You can only add up to ${maxFamilyMembers} family members.`}
            title="Add More Family Members"
          />,
        );
      }

      setQuickAddRelation(relation);
      setQuickAddSelectedPerson(nodeDetails);
      setQuickAddOpen(true);
    },
    [familyTree?.members, isPaidUser, openModal, closeModal],
  );

  if (isLoading || isTreeDeleted) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100dvh',
          mt: '64px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return <ErrorDisplay error={error} onRetry={refetch} />;
  }

  function openSearchModal(): void {
    openModal(
      <MemberSearch
        closeModal={closeModal}
        members={familyTree?.members}
        setRootId={setRootId}
      />,
    );
  }

  function handleOpenNarrative(type: 'story' | 'journey', id: string): void {
    setSelectedNarrativeType(type);
    setSelectedNarrativeId(id);
    setValue(type === 'story' ? '3' : '4');
    closeDrawer();
  }

  function handleDeleteMember(payload: DeleteMemberPayload): void {
    deleteTreeMemberMutation.mutate(payload, {
      onSuccess: () => {
        enqueueSnackbar('Family member deleted successfully!', {
          variant: 'success',
        });
        queryClient.refetchQueries({
          queryKey: ['familyTrees', treeId],
          exact: true,
        });
        closeModal?.();
      },
      onError: (error) => {
        enqueueSnackbar('Failed to delete family member', {
          variant: 'error',
        });
        console.error('Error deleting member:', error);
      },
    });
  }

  function handleDeleteTree() {
    // Success/error snackbars come from useApiMutation, so only log here.
    deleteFamilyTreeMutation.mutate(undefined, {
      onSuccess: () => {
        // Unmount tree-dependent children and disable their queries first
        setIsTreeDeleted(true);
        closeModal?.();
        navigate('/app', { replace: true });
        // Drop every cached query for this tree once the page is gone
        setTimeout(() => {
          queryClient.removeQueries({
            predicate: (query) => query.queryKey[1] === treeId,
          });
          queryClient.invalidateQueries({
            queryKey: ['familyTrees', currentUser?.uid],
          });
        }, 0);
      },
      onError: (error) => {
        console.error('Error deleting tree:', error);
      },
    });
  }

  function handleUpdateTree(payload: UpdateTreePayload): void {
    updateTreeMutation.mutate(payload, {
      onSuccess: () => {
        enqueueSnackbar('Family tree updated successfully!', {
          variant: 'success',
        });
        queryClient.refetchQueries({
          queryKey: ['familyTrees', treeId],
          exact: true,
        });
      },
      onError: (error) => {
        enqueueSnackbar('Failed to update family tree', {
          variant: 'error',
        });
        console.error('Error updating tree:', error);
      },
    });
  }

  function handleAddCollaborators(payload: AddCollaboratorsPayload): void {
    addCollaboratorsMutation.mutate(payload, {
      onSuccess: () => {
        enqueueSnackbar('Collaborator added successfully!', {
          variant: 'success',
        });
        queryClient.refetchQueries({
          queryKey: ['familyTrees', treeId],
          exact: true,
        });
        closeModal?.();
      },
      onError: (error) => {
        enqueueSnackbar('Failed to add collaborator', {
          variant: 'error',
        });
        console.error('Error adding collaborator:', error);
      },
    });
  }

  function openDeleteMemberModal(name: string, nodeId: string): void {
    openModal(
      <DeleteMember
        closeModal={closeModal}
        name={name}
        nodeId={nodeId}
        onDelete={handleDeleteMember}
        rootId={firstRootId ?? ''}
      />,
    );
  }

  function openDeleteTreeModal(): void {
    openModal(
      <DeleteTree closeModal={closeModal} onDelete={handleDeleteTree} />,
    );
  }
  function openAddCollaboratorsModal(): void {
    openModal(
      <AddCollaborators
        closeModal={closeModal}
        onAddCollaborators={handleAddCollaborators}
        treeName={familyTree?.name || ''}
      />,
    );
  }

  function navToAddMember(): void {
    setValue('2');
    openDrawer(rootId);
  }

  const handleDownload = async () => {
    if (!isPaidUser) {
      return openModal(
        <SubscribeModal
          closeModal={closeModal}
          handleClose={closeModal}
          message={
            'Dowload PDF copy of your family is unavailable on free plan.'
          }
          title="Download Your Family Tree and Share with your family members"
        />,
      );
    }
    const node = treeRef.current;
    if (!node) return;

    // Wait for all images from GCP to load
    const images = node.querySelectorAll<HTMLImageElement>('img');
    await Promise.all(
      Array.from(images).map(
        (img) =>
          new Promise<void>((resolve) => {
            if (img.complete) {
              resolve();
            } else {
              img.onload = () => resolve();
              img.onerror = () => resolve();
            }
          }),
      ),
    );

    try {
      const dataUrl = await domtoimage.toJpeg(node, {
        quality: 0.95,
        bgcolor: '#fff',
        width: node.scrollWidth,
        height: node.scrollHeight,
      });

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const filename = `legacyties-${timestamp}.jpg`;

      const link = document.createElement('a');
      link.download = filename;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Error generating image:', err);
    }
  };

  const handleGedcomExport = async () => {
    if (!isPaidUser) {
      return openModal(
        <SubscribeModal
          closeModal={closeModal}
          handleClose={closeModal}
          message={
            'Dowload GEDCOM data of your family tree is unavailable on free plan.'
          }
          title="Download Your Family Tree and Share with your family members"
        />,
      );
    }

    if (!treeId) return;

    try {
      enqueueSnackbar('Generating GEDCOM file...', { variant: 'info' });

      const response = await exportGedcom(treeId);

      const blob = response.data;

      const contentDisposition = response.headers['content-disposition'];

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      let filename = `legacyties-${timestamp}.ged`;

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match?.[1]) {
          filename = match[1].toLowerCase();
        }
      }

      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();

      URL.revokeObjectURL(url);

      enqueueSnackbar('GEDCOM exported successfully', {
        variant: 'success',
      });
    } catch (error) {
      console.error('Error generating GEDCOM:', error);
      enqueueSnackbar('Failed to generate GEDCOM file', {
        variant: 'error',
      });
    }
  };

  const isTreeTab = value === '2';

  return (
    <Box
      component="section"
      sx={{
        flexGrow: 1,
        minHeight: 0,
        overflow: isTreeTab ? 'clip' : 'auto',
        width: '100%',
        height: isTreeTab ? '100dvh' : undefined,
      }}
    >
      <Helmet>
        <title>Family tree | {AppConfig.appName}</title>
        <meta name="description" content="View your family tree" />
      </Helmet>
      <Stack
        spacing={2}
        sx={{
          alignItems: 'center',
          mx: 3,
          pb: isTreeTab ? 0 : 5,
          mt: { xs: 8, md: 0 },
          display: isTreeTab ? 'flex' : undefined,
          flexDirection: isTreeTab ? 'column' : undefined,
          height: isTreeTab
            ? { xs: 'calc(100% - 64px)', md: '100%' }
            : undefined,
          minHeight: isTreeTab ? 0 : undefined,
        }}
      >
        <Header
          title={familyTree?.name ?? ''}
          headerName="Family tree"
          allowSideIcon={false}
        />
        <Box
          sx={{
            width: '100%',
            maxWidth: isTreeTab ? 'none' : { sm: '100%', md: '1700px' },
            display: isTreeTab ? 'flex' : undefined,
            flexDirection: isTreeTab ? 'column' : undefined,
            flexGrow: isTreeTab ? 1 : undefined,
            minHeight: isTreeTab ? 0 : undefined,
          }}
        >
          <Typography
            component="h2"
            variant="h6"
            sx={{ mb: 2, display: { md: 'none' } }}
          >
            {familyTree?.name ?? ''}
          </Typography>
          <TabContext value={value}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <TabList onChange={handleChange} aria-label="family tree tabs">
                <Tab label={t('tree:treeId.navItems.overview')} value="1" />
                <Tab label={t('tree:treeId.navItems.tree')} value="2" />
                <Tab
                  label={t('tree:treeId.navItems.stories')}
                  value="3"
                  sx={{ display: isOwner ? 'inline-flex' : 'none' }}
                />
                <Tab
                  label={t('tree:treeId.navItems.journeyMap')}
                  value="4"
                  sx={{ display: isOwner ? 'inline-flex' : 'none' }}
                />
              </TabList>
            </Box>
            <TabPanel value="1" sx={{ p: { xs: '0' } }}>
              <FamilyTreeOverview
                initialData={familyTree}
                openDeleteTreeModal={openDeleteTreeModal}
                openAddCollaboratorsModal={openAddCollaboratorsModal}
                onSave={handleUpdateTree}
                navToAddMember={navToAddMember}
                handleGedcomExport={handleGedcomExport}
              />
            </TabPanel>
            <TabPanel
              value="2"
              sx={{
                p: 0,
                flex: isTreeTab ? 1 : undefined,
                minHeight: isTreeTab ? 0 : undefined,
                overflow: isTreeTab ? 'clip' : undefined,
                contain: isTreeTab ? 'paint' : undefined,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  height: '100%',
                  minHeight: 0,
                  backgroundColor: palette.background.paper,
                  cursor: 'grab',
                  '&:active': {
                    cursor: 'grabbing',
                  },
                }}
              >
                {nodes.length > 0 ? (
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      height: '100%',
                      flex: 1,
                      minHeight: 0,
                      overflow: 'clip',
                      contain: 'paint',
                      '& .react-transform-wrapper, & .react-transform-component':
                        {
                          width: '100%',
                          height: '100%',
                        },
                    }}
                  >
                    {nodes.length > LARGE_TREE_THRESHOLD && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 16,
                          zIndex: 2,
                        }}
                      >
                        <Select
                          size="small"
                          value={maxDepth === null ? 'all' : String(maxDepth)}
                          onChange={(e) =>
                            setDepthChoice(
                              e.target.value === 'all'
                                ? null
                                : Number(e.target.value),
                            )
                          }
                          sx={{ backgroundColor: palette.background.paper }}
                        >
                          {[1, 2, 3, 4, 5].map((d) => (
                            <MenuItem key={d} value={String(d)}>
                              {d} generation{d > 1 ? 's' : ''} from root
                            </MenuItem>
                          ))}
                          <MenuItem value="all">Entire tree</MenuItem>
                        </Select>
                      </Box>
                    )}
                    <TransformWrapper
                      key={`${rootId}-${displayNodes.length}`}
                      velocityAnimation={{
                        disabled: true,
                      }}
                      alignmentAnimation={{
                        disabled: true,
                      }}
                      zoomAnimation={{
                        disabled: true,
                      }}
                      doubleClick={{
                        disabled: true,
                      }}
                      limitToBounds={false}
                      minScale={MIN_SCALE}
                      maxScale={MAX_SCALE}
                      wheel={{ step: 0.02 }}
                    >
                      {({ zoomIn, zoomOut, resetTransform, setTransform }) => (
                        <TreeWrapper
                          zoomIn={zoomIn}
                          zoomOut={zoomOut}
                          resetTransform={resetTransform}
                          setTransform={setTransform}
                          resetRootId={rootId !== firstRootId}
                          resetRootHandler={resetRootHandler}
                          openSearchModal={openSearchModal}
                          handleDownload={handleDownload}
                          handleGedcomExport={handleGedcomExport}
                          treeBounds={treeBounds}
                          treeKey={`${rootId}-${displayNodes.length}`}
                        >
                          <>
                            <TransformComponent>
                              <Box
                                ref={treeRef}
                                sx={{
                                  width: treeBounds.width,
                                  height: treeBounds.height,
                                  display: 'flex',
                                  flexDirection: 'row',
                                  flexWrap: 'wrap',
                                  alignContent: 'flex-start',
                                }}
                              >
                                {rootId && (
                                  <ReactFamilyTree
                                    key={`${rootId}-${displayNodes.length}`}
                                    nodes={displayNodes}
                                    rootId={rootId}
                                    width={NODE_WIDTH}
                                    height={NODE_HEIGHT}
                                    renderNode={(node: Readonly<ExtNode>) => (
                                      <FamilyNode
                                        key={node.id}
                                        node={node}
                                        nodeDetails={familyTree?.members?.find(
                                          (member: Person) =>
                                            member.id === node.id,
                                        )}
                                        isRoot={node.id === rootId}
                                        isSelected={node.id === selectedNode}
                                        onClick={openDrawer}
                                        onSubClick={setRootId}
                                        onQuickAdd={handleQuickAdd}
                                        style={{
                                          width: NODE_WIDTH,
                                          height: NODE_HEIGHT,
                                          transform: `translate(${
                                            node.left * (NODE_WIDTH / 2) -
                                            treeBounds.left
                                          }px, ${
                                            node.top * (NODE_HEIGHT / 2) -
                                            treeBounds.top
                                          }px)`,
                                        }}
                                      />
                                    )}
                                  />
                                )}
                              </Box>
                            </TransformComponent>
                          </>
                        </TreeWrapper>
                      )}
                    </TransformWrapper>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      flexDirection: 'column',
                      textAlign: 'center',
                      color: palette.text.secondary,
                    }}
                  >
                    <Typography variant="h6" gutterBottom>
                      No family tree data available
                    </Typography>
                  </Box>
                )}
              </Box>
            </TabPanel>
            {isOwner && (
              <>
                <TabPanel value="3" sx={{ p: { xs: '0' } }}>
                  <FamilyStory
                    treeId={treeId ?? ''}
                    treeMembers={familyTree?.members ?? []}
                    autoOpenCreate={shouldAutoOpenStory}
                    isActive={value === '3'}
                    preselectedStoryId={
                      selectedNarrativeType === 'story'
                        ? selectedNarrativeId
                        : undefined
                    }
                  />
                </TabPanel>
                <TabPanel value="4" sx={{ p: { xs: '0' } }}>
                  <MigrationTracking
                    treeId={treeId ?? ''}
                    treeMembers={familyTree?.members ?? []}
                    autoOpenCreate={shouldAutoOpenJourney}
                    isActive={value === '4'}
                    preselectedRecordId={
                      selectedNarrativeType === 'journey'
                        ? selectedNarrativeId
                        : undefined
                    }
                  />
                </TabPanel>
              </>
            )}
          </TabContext>
        </Box>
        <Drawer
          anchor="right"
          open={isDrawerOpen}
          onClose={closeDrawer}
          PaperProps={{
            sx: { width: { xs: 300, md: 500 }, padding: 2 },
          }}
        >
          <EditFamilyMember
            defaultValues={familyTree?.members?.find(
              (member: Person) => member.id === selectedNode,
            )}
            closeDrawer={closeDrawer}
            onAddMember={handleAddMember}
            treeMembers={familyTree?.members}
            rootId={rootId ?? ''}
            setRootId={setRootId}
            openDeleteMemberModal={openDeleteMemberModal}
            firstRootId={firstRootId ?? ''}
            treeId={treeId ?? ''}
            resetRootHandler={resetRootHandler}
            collaborators={familyTree?.collaborators || []}
            onOpenNarrative={handleOpenNarrative}
          />
        </Drawer>
        <AddMemberDialog
          isAddMemberOpen={quickAddOpen}
          setIsAddMemberOpen={setQuickAddOpen}
          familyType={quickAddRelation}
          handleAddMember={handleAddMember}
          selectedPerson={quickAddSelectedPerson}
          treeMembers={familyTree?.members}
          loading={mutation.isPending}
          memberName={
            quickAddSelectedPerson
              ? `${quickAddSelectedPerson.first_name} ${quickAddSelectedPerson.last_name}`
              : ''
          }
        />
      </Stack>
    </Box>
  );
}
